/**
 * I18N-C2 — deterministic canonical narrative localization.
 *
 * Presentation only.
 *
 * Rules:
 * - NEVER mutates lb.result.v299 or any engine output.
 * - NEVER imports engine / DB mirrors / storage / migration / adapter code.
 * - zh returns canonical source text verbatim.
 * - en translates only exact audited canonical strings.
 * - unknown text falls back verbatim.
 * - no substring replacement, parsing, normalization or AI translation.
 *
 * C2-A scope:
 * - Sheet12 annualAdvice finite narrative only.
 */

const pairs = (rows) =>
  Object.freeze(Object.fromEntries(rows.map(([source, english]) => [source, english])));

const SHEET12 = pairs([
  // Personal Year 1
  ["开创·主动·新计划", "Initiation · Proactivity · New Plans"],
  ["启动新项目，建立个人品牌，主动拓展人脉，勇于提出想法", "Launch new projects, build your personal brand, proactively expand your network, and confidently put forward ideas."],
  ["拖延决定，守旧等待，过度依赖他人，被动观望", "Delaying decisions, waiting conservatively, over-relying on others, and remaining passive."],
  ["今年适合晋升或换新赛道，主动争取机会，展现领导力", "This year is suitable for pursuing promotion or moving into a new track. Proactively seek opportunities and demonstrate leadership."],
  ["投资前期布局，减少负债，建立收入新来源", "Focus on early-stage investment planning, reduce debt, and build new income sources."],
  ["学习新领域入门，建立知识体系框架", "Begin learning in a new field and build a structured knowledge framework."],
  ["主动结交新圈子，打破旧有关系局限", "Proactively build new circles and move beyond the limits of existing relationships."],
  ["非常适合创业第一步，或在职内开启副业测试", "Highly suitable for taking the first step into entrepreneurship or testing a side business while employed."],
  ["制定三年目标；本月确定一个核心方向；建立个人品牌档案", "Set a three-year goal; choose one core direction this month; create a personal brand profile."],

  // Personal Year 2
  ["合作·耐心·人际布局", "Collaboration · Patience · Relationship Building"],
  ["建立合伙关系，深化现有人脉，合作谈判，团队建设", "Build partnerships, deepen existing relationships, conduct collaborative negotiations, and strengthen the team."],
  ["冲动决策，单打独斗，强行推进大计划", "Impulsive decisions, going it alone, and forcing major plans forward."],
  ["寻找合适合伙人，强化团队协作，深耕现有职场关系", "Identify suitable partners, strengthen teamwork, and deepen existing workplace relationships."],
  ["保守财务策略，避免单一投资，寻找合作型财富来源", "Use a conservative financial approach, avoid relying on a single investment, and explore collaborative income opportunities."],
  ["与他人协作学习，参与团队培训或读书会", "Learn collaboratively with others and participate in team training or study groups."],
  ["修复重要关系，培养深度伙伴，减少无效社交", "Repair important relationships, develop deeper partnerships, and reduce low-value social activity."],
  ["适合与人合伙创业，但需谨慎选择伙伴，明确权责", "Suitable for partnership-based entrepreneurship, with careful partner selection and clearly defined responsibilities."],
  ["审视现有合作关系；签订一份重要合约；优化团队分工", "Review existing partnerships; formalize one important agreement; improve team role allocation."],

  // Personal Year 3
  ["表达·宣传·学习输出", "Expression · Promotion · Knowledge Sharing"],
  ["发布内容，公开演讲，学习输出，品牌宣传，自我展示", "Publish content, speak publicly, share what you learn, promote your brand, and increase visibility."],
  ["内敛压抑，只学不做，负面情绪蔓延", "Holding back expression, learning without acting, and allowing negative emotions to spread."],
  ["内容变现机会强，适合做个人IP或品牌露出", "There are stronger opportunities to monetize content, making this a suitable time to build personal-brand visibility."],
  ["多元收入测试期，内容与知识变现，避免大额投资", "A period for testing diversified income and monetizing content or knowledge; avoid large investments."],
  ["学习表达技巧，写作、演讲、视频创作，输出见解", "Develop communication skills through writing, speaking, video creation, and sharing insights."],
  ["扩展社交圈，参加行业活动，建立可见度", "Expand your network, attend industry events, and increase visibility."],
  ["适合开始自媒体创业，测试市场反应", "Suitable for starting a creator-led venture and testing market response."],
  ["发布30篇内容；参加3场行业活动；建立邮件/粉丝列表", "Publish 30 pieces of content; attend three industry events; build an email or follower list."],

  // Personal Year 4
  ["稳定·制度·打基础", "Stability · Systems · Foundation Building"],
  ["建立系统流程，制度化运营，专注深耕，稳步积累", "Build systematic processes, formalize operations, deepen your focus, and accumulate steadily."],
  ["频繁跳槽，冒险投资，分散精力", "Frequent job changes, speculative investments, and spreading your attention too thin."],
  ["深耕专业技能，建立标准流程，稳步晋升", "Deepen professional expertise, establish standard processes, and pursue steady career progression."],
  ["减少消费，增加储蓄，建立被动收入基础", "Reduce unnecessary spending, increase savings, and build a foundation for passive income."],
  ["深度学习某一领域，考取证书，建立专业权威", "Study one field in depth, pursue relevant certification, and build professional credibility."],
  ["维护稳定的核心圈子，减少无效社交", "Maintain a stable core network and reduce low-value social activity."],
  ["不是最佳创业年，但适合为创业做准备和测试", "Not the strongest year for a full entrepreneurial launch, but suitable for preparation and testing."],
  ["完成一项专业认证；优化核心工作流程；建立储蓄目标", "Complete one professional certification; optimize a core workflow; establish a savings target."],

  // Personal Year 5
  ["变化·转换·流动机会", "Change · Transition · Emerging Opportunities"],
  ["尝试新事物，转换赛道，旅行、灵感突破，抓住机遇", "Try new things, explore a new track, travel, seek fresh inspiration, and act on suitable opportunities."],
  ["固守不变，拒绝新机会，过于保守", "Remaining rigid, rejecting new opportunities, and being overly conservative."],
  ["今年适合职业转型或跨界，勇于抓住突然出现的机会", "This year is suitable for career transition or cross-disciplinary moves; be ready to act on emerging opportunities."],
  ["灵活理财，短线机会多，但需警惕冲动消费", "Keep financial planning flexible; short-term opportunities may arise, but watch for impulsive spending."],
  ["多元化学习，跨领域探索，打开认知边界", "Diversify your learning, explore across fields, and broaden your perspective."],
  ["开放结交新型人脉，不拘泥旧圈子", "Stay open to new relationships and avoid limiting yourself to familiar circles."],
  ["高适合年：今年创业或转职成功率高", "A highly suitable year for entrepreneurship or career change, with comparatively stronger conditions for success."],
  ["测试一个新业务方向；参加跨行业交流活动；制定弹性计划", "Test one new business direction; attend a cross-industry networking event; create a flexible plan."],

  // Personal Year 6
  ["责任·家庭·服务·团队", "Responsibility · Family · Service · Team"],
  ["承担责任，服务他人，家庭布局，团队建设", "Take responsibility, support others, plan around family priorities, and strengthen the team."],
  ["逃避责任，只顾自我利益，拒绝给予", "Avoiding responsibility, focusing only on self-interest, and being unwilling to contribute."],
  ["适合在组织内承担更多职责，团队管理，客户服务", "Suitable for taking on greater responsibility within an organization, managing teams, and serving clients."],
  ["家庭投资，保险规划，稳健理财", "Focus on family-oriented financial planning, insurance planning, and prudent money management."],
  ["学习领导力、沟通协调，家庭教育类课程", "Develop leadership and coordination skills, including communication and family-education related learning."],
  ["深化家庭与团队关系，成为圈子中的支持者", "Deepen family and team relationships and become a reliable source of support within your circle."],
  ["创业偏难，但适合作为团队支柱，或开办服务型小企业", "Entrepreneurship may be more demanding, while service-oriented small businesses or strong team-support roles may be more suitable."],
  ["优化工作与生活平衡；制定家庭财务计划；负责一个团队项目", "Improve work-life balance; create a family financial plan; take responsibility for one team project."],

  // Personal Year 7
  ["沉淀·学习·研究·修正", "Reflection · Learning · Research · Refinement"],
  ["深度学习，内省研究，修正方向，精进技能", "Study deeply, reflect, research, refine your direction, and strengthen your skills."],
  ["强行冲刺，大额消费，四处表现", "Forcing rapid progress, making large discretionary expenditures, and seeking visibility everywhere."],
  ["低调专注，内部提升，积累深度能力", "Stay focused and understated, strengthen internally, and build deeper capability."],
  ["保守理财年，避免大型投资或高风险操作", "A year for conservative financial management; avoid large investments or high-risk activity."],
  ["最佳学习年：深读经典，研究前沿，参加系统课程", "A strong year for learning: study foundational works, research emerging developments, and take structured courses."],
  ["减少无效社交，与智者深交，整理旧有关系", "Reduce low-value social activity, build deeper relationships with knowledgeable people, and review existing connections."],
  ["暂不建议开创新业务，适合做深度研究和规划", "Starting a new venture is not currently recommended; this period is better suited to deeper research and planning."],
  ["完成一项专业深度学习；写一份个人发展规划；整理资产和关系", "Complete one in-depth professional learning goal; write a personal development plan; organize your assets and relationships."],

  // Personal Year 8
  ["事业·财富·权力·成果", "Career · Wealth · Influence · Results"],
  ["全力冲刺，争取成果，晋升，大型合作，财富扩张", "Push decisively toward results, pursue advancement, develop major collaborations, and expand financial capacity."],
  ["拖延保守，错失机会，消极等待", "Delaying excessively, being overly conservative, missing opportunities, and waiting passively."],
  ["今年是事业高峰年：主动争取晋升或合同，建立权威", "This is a strong career-results year: proactively pursue promotion or important contracts and strengthen professional authority."],
  ["积极理财，扩大投资，财富丰收期", "Take a more active approach to financial planning and investment expansion while maintaining appropriate risk controls."],
  ["学习财务管理、领导力、谈判技巧", "Develop financial management, leadership, and negotiation skills."],
  ["结交权贵资源，强化商业人脉", "Build relationships with influential resources and strengthen your business network."],
  ["今年最适合正式创业，签约大客户，或接受投资", "This is one of the more suitable years for formally launching a venture, signing major clients, or considering investment."],
  ["谈一笔重要合同；拓展一条新收入线；达成年度最高业绩", "Negotiate one important contract; develop one new income stream; pursue your strongest annual performance."],

  // Personal Year 9
  ["结束·整理·转型·放下", "Completion · Organization · Transition · Release"],
  ["整理旧有关系，结束低效项目，为新周期做准备", "Review existing relationships, close low-value projects, and prepare for a new cycle."],
  ["执着过去，开始重大新项目，大额投资", "Holding on to the past, starting major new projects, and making large investments."],
  ["收尾旧项目，整理职业资产，为下一轮做好准备", "Complete existing projects, organize career assets, and prepare for the next stage."],
  ["收回投资，减少负债，保守理财", "Consolidate investments, reduce debt, and use a conservative financial approach."],
  ["整合过去所学，梳理知识体系，沉淀总结", "Integrate what you have learned, organize your knowledge system, and consolidate key lessons."],
  ["了结旧有矛盾，感恩告别，清理低质量关系", "Resolve old conflicts, close relationships with gratitude where appropriate, and reduce low-quality connections."],
  ["不适合开新业务，适合结束低效业务，做好交接", "Not suitable for launching a major new business; better suited to closing inefficient activities and completing transitions."],
  ["回顾过去9年成就；整理资产与关系清单；写下新周期目标", "Review achievements from the past nine years; organize an asset and relationship inventory; write goals for the next cycle."],

  // Sheet12 five-element supplemental rows
  ["缺乏自信、优柔寡断、创新不足", "Low confidence, indecision, and insufficient innovation."],
  ["教育培训、管理顾问、创业、内容创作", "Education & Training, Management Consulting, Entrepreneurship, Content Creation"],
  ["长期投资、注重成长型资产", "Long-term investing with an emphasis on growth-oriented assets."],

  ["缺乏热情、执行力弱、社交困难", "Low enthusiasm, weaker execution, and difficulty with social engagement."],
  ["销售、市场营销、媒体、个人品牌", "Sales, Marketing, Media, Personal Branding"],
  ["积极把握机会，注意风险控制", "Actively pursue suitable opportunities while maintaining risk control."],

  ["缺乏稳定性、信任危机、执行力差", "Insufficient stability, trust challenges, and weaker execution."],
  ["运营管理、品质管理、行政、房地产", "Operations Management, Quality Management, Administration, Real Estate"],
  ["稳健保守投资，注重资产安全", "Use a prudent investment approach with an emphasis on asset security."],

  ["缺乏执行力、优柔寡断、缺乏原则", "Weak execution, indecision, and insufficient consistency in principles."],
  ["品质管理、法律合规、财务、精密制造", "Quality Management, Legal & Compliance, Finance, Precision Manufacturing"],
  ["价值投资，注重资产质量", "Focus on value-oriented investing and asset quality."],

  ["缺乏灵活性、固执、学习困难", "Limited flexibility, rigidity, and difficulty adapting to learning."],
  ["顾问服务、贸易、互联网、数据分析", "Consulting Services, Trading, Internet, Data Analysis"],
  ["多元化投资，善于把握市场机会", "Use diversified investing and stay alert to suitable market opportunities."],
]);


const SHEET13 = pairs([
  // Life Number — B20 / B21 / B33
  ["主动开创型", "Proactive Initiator"],
  ["具备天然的领导力与独立决断力，适合以主动性打开局面。", "Natural leadership and independent decision-making support a proactive approach to creating momentum."],
  ["避免过度独断、急于求成、忽视团队配合。", "Avoid becoming overly unilateral, rushing results, or overlooking team coordination."],

  ["合作协调型", "Collaborative Coordinator"],
  ["善于协作与调和，在关系与团队中发挥最大价值。", "Strong collaboration and coordination skills create the greatest value through relationships and teamwork."],
  ["避免过于依赖他人、缺乏主见、拖延决断。", "Avoid excessive dependence on others, weak independent judgment, or delayed decisions."],

  ["创意表达型", "Creative Communicator"],
  ["富有创造力和表达欲，适合通过内容与沟通构建影响力。", "Creativity and a strong drive for expression are well suited to building influence through content and communication."],
  ["避免分散精力、缺乏专注、情绪化决策。", "Avoid spreading your attention too widely, losing focus, or making emotion-driven decisions."],

  ["稳健建设型", "Steady Builder"],
  ["重视规则与基础，擅长建立制度与长期积累。", "A strong focus on structure and fundamentals supports systematic development and long-term accumulation."],
  ["避免过度保守、拒绝变化、固执一己之见。", "Avoid excessive conservatism, resistance to change, or becoming overly fixed in your own view."],

  ["变革适应型", "Adaptive Change Driver"],
  ["喜爱变化与探索，适合灵活切换赛道和抓住流动机会。", "A preference for change and exploration supports flexible transitions and the ability to act on emerging opportunities."],
  ["避免冲动决策、频繁更换方向、缺乏坚持力。", "Avoid impulsive decisions, changing direction too frequently, or failing to sustain commitment."],

  ["责任服务型", "Responsible Service Builder"],
  ["重视家庭与团队，适合在服务与责任中找到价值感。", "A strong sense of responsibility toward family and team supports creating value through service and commitment."],
  ["避免过度付出导致精力耗尽，学会合理界限。", "Avoid over-giving to the point of exhaustion; maintain appropriate boundaries."],

  ["深度研究型", "Deep Researcher"],
  ["具有强烈的研究欲与分析力，适合建立深度知识体系。", "Strong curiosity and analytical ability support building deep, structured expertise."],
  ["避免过度封闭、回避社交、陷入分析瘫痪。", "Avoid becoming overly isolated, withdrawing from interaction, or getting trapped in analysis paralysis."],

  ["财富成就型", "Achievement & Wealth Builder"],
  ["目标导向强，适合在商业与财富方向实现大格局突破。", "Strong goal orientation supports pursuing larger-scale breakthroughs in business and wealth development."],
  ["避免只追求金钱而忽视关系经营与健康管理。", "Avoid pursuing financial results at the expense of relationships and health management."],

  ["完结蜕变型", "Completion & Transformation"],
  ["具有人道精神与整合力，适合在服务与蜕变中完成使命。", "A strong capacity for integration and service supports meaningful transformation and broader contribution."],
  ["避免执着于旧有模式、难以放手、陷入完美主义。", "Avoid holding too tightly to old patterns, struggling to let go, or falling into perfectionism."],

  // Current Goal — B22 / B23
  ["事业突破策略", "Career Breakthrough Strategy"],
  ["以当前专业能力为核心，有策略地争取晋升与影响力扩张。", "Use your current professional strengths as the foundation for strategically pursuing advancement and greater influence."],

  ["财富积累策略", "Wealth Accumulation Strategy"],
  ["围绕收入增长与资产积累设计多元财富路径。", "Build diversified wealth pathways around income growth and asset accumulation."],

  ["创业验证策略", "Entrepreneurial Validation Strategy"],
  ["用精益方式低成本验证创业方向，降低风险。", "Validate entrepreneurial ideas through lean, low-cost testing to reduce risk."],

  ["学习结构化策略", "Structured Learning Strategy"],
  ["建立系统化学习框架，实现知识到能力的高效转化。", "Build a structured learning framework that converts knowledge into practical capability efficiently."],

  ["稳健经营策略", "Stability-Focused Strategy"],
  ["优先确保生活质量和工作稳定性，构建安全感基础。", "Prioritize quality of life and work stability to build a stronger foundation of security."],

  ["人脉资源策略", "Network & Relationship Strategy"],
  ["重点经营高质量关系，通过贵人与合作伙伴放大个人能量。", "Focus on high-quality relationships and use mentors and partners to extend your capabilities and reach."],

  ["内容变现策略", "Content Monetization Strategy"],
  ["以内容为核心建立个人影响力，并探索多元变现路径。", "Build personal influence around content and explore multiple monetization pathways."],

  ["领导力发展策略", "Leadership Development Strategy"],
  ["以系统思维提升团队领导能力，发挥大格局价值。", "Use systems thinking to strengthen team leadership and create value at a broader organizational level."],

  // Five Element Need — B24 / B25 / B29
  ["成长学习与规划型策略", "Growth, Learning & Planning Strategy"],
  ["当前发展需要更多成长结构与长期规划支持。", "Current development benefits from stronger growth structures and longer-term planning."],
  ["系统学习新技能；制定3-5年发展路线；发展教育、咨询或内容方向。", "Learn new skills systematically; create a three-to-five-year development roadmap; explore education, consulting, or content-related directions."],

  ["表达营销与能见度策略", "Expression, Marketing & Visibility Strategy"],
  ["当前发展需要更多热情、表达和公开露出来提升影响力。", "Current development benefits from stronger expression, visibility, and public-facing communication."],
  ["主动做内容输出与品牌建设；参加公开分享活动；强化营销推广。", "Create content proactively, build your brand, participate in public-sharing opportunities, and strengthen marketing activity."],

  ["稳健积累与基础建设策略", "Stable Accumulation & Foundation Strategy"],
  ["当前发展需要更稳固的根基、制度和资产支撑。", "Current development benefits from stronger foundations, systems, and asset support."],
  ["建立系统流程与制度；积累固定资产与长期资本；强化财务管理。", "Build systematic processes and structures; accumulate long-term assets and capital; strengthen financial management."],

  ["精准突破与专业提升策略", "Precision & Professional Development Strategy"],
  ["当前发展需要更清晰的边界感、专业技能和逻辑能力。", "Current development benefits from clearer boundaries, stronger professional expertise, and more rigorous thinking."],
  ["聚焦核心专业领域；建立精准执行系统；发展金融、法律或技术能力。", "Focus on a core professional field; build a precise execution system; strengthen finance, legal, or technical capabilities."],

  ["流通变现与人脉沟通策略", "Flow, Monetization & Networking Strategy"],
  ["当前发展需要更多流动性、沟通能力和变现渠道。", "Current development benefits from greater flexibility, communication capacity, and monetization channels."],
  ["拓展人脉与渠道；强化销售或贸易能力；增加收入来源的多元性。", "Expand networks and channels; strengthen sales or trading capability; diversify income sources."],

  // Venture Index Band — B26
  ["大力推进策略", "Strong Advancement Strategy"],
  ["积极配合策略", "Active Development Strategy"],
  ["谨慎测试策略", "Cautious Testing Strategy"],
  ["补足资源策略", "Resource-Building Strategy"],
  ["蓄势待发策略", "Preparation Strategy"],

  // Day Master Status — B27
  ["学习积累型策略", "Learning & Accumulation Strategy"],
  ["稳健均衡型策略", "Steady & Balanced Strategy"],
  ["主动扩张型策略", "Proactive Expansion Strategy"],

  // Personal Year — B28 / B30
  ["开创年：主动布局", "Initiation Year: Build Proactively"],
  ["今年是新周期起点，一切主动出击的行动都有更好的土壤。", "This year marks the start of a new cycle, providing stronger conditions for proactive initiatives."],

  ["合作年：深化关系", "Collaboration Year: Deepen Relationships"],
  ["今年适合强化合作伙伴与人脉关系，耐心布局。", "This year favors strengthening partnerships and networks through patient relationship building."],

  ["表达年：内容输出", "Expression Year: Create & Share"],
  ["今年适合大量输出内容、建立曝光度和传播影响力。", "This year favors consistent content creation, stronger visibility, and broader communication influence."],

  ["稳固年：夯实基础", "Foundation Year: Strengthen the Base"],
  ["今年适合建立制度、深耕专业、稳步积累而非冒险扩张。", "This year favors building systems, deepening expertise, and accumulating steadily rather than expanding aggressively."],

  ["变化年：抓住机遇", "Change Year: Capture Opportunities"],
  ["今年变化机会多，适合尝试新方向但需控制风险。", "This year may bring more change opportunities; exploring new directions can be suitable when risk is controlled."],

  ["责任年：服务贡献", "Responsibility Year: Serve & Contribute"],
  ["今年适合承担更多责任、服务团队或家庭，增强领导信任。", "This year favors taking on greater responsibility, supporting team or family, and strengthening leadership trust."],

  ["沉淀年：深度学习", "Reflection Year: Deep Learning"],
  ["今年适合低调沉淀、专注研究和自我修正，而非强行冲刺。", "This year favors focused learning, research, and self-refinement rather than forcing rapid expansion."],

  ["丰收年：全力冲刺", "Results Year: Drive for Outcomes"],
  ["今年是事业财富高峰年，全力出击，争取最大成果。", "This year carries stronger career and wealth momentum, supporting focused pursuit of meaningful results."],

  ["收尾年：整理放下", "Completion Year: Organize & Release"],
  ["今年适合整理旧有模式，结束低效项目，为新周期清场。", "This year favors reviewing old patterns, closing low-value projects, and preparing for a new cycle."],

  // Sheet13 riskAlert / responseAdvice — exact DB_RISK_ALERTS lookups
  ["执行力和承压能力可能不足，重大转变前需先补充资源与支持系统。", "Execution capacity and resilience under pressure may be limited. Strengthen resources and support systems before making major changes."],
  ["先建立学习、贵人、资金和时间缓冲再行动；避免孤注一掷。", "Build learning, mentor, financial, and time buffers before acting; avoid committing everything to a single move."],

  ["精力充沛但可能过于强硬，容易在合作关系中产生摩擦。", "Strong energy and drive may become overly forceful, which can create friction in collaborative relationships."],
  ["注意团队协作与沟通柔性；避免独断专行；留意人际关系经营。", "Maintain teamwork and communication flexibility; avoid overly unilateral decisions; pay attention to relationship management."],

  ["当前日主状态相对平衡，整体风险较低，重点在持续执行与避免分散。", "The current Day Master state is relatively balanced, with lower overall risk. The priority is consistent execution and avoiding unnecessary dispersion of focus."],
  ["保持稳定节奏，按阶段执行计划，避免频繁改变方向。", "Maintain a steady pace, execute the plan by stages, and avoid changing direction too frequently."],
]);


const SHEET14 = pairs([
  // 事业发展
  ["定位与整理", "Positioning & Review"],
  ["梳理个人能力、行业经验、核心优势和可转化价值。", "Review your capabilities, industry experience, core strengths, and transferable value."],
  ["完成能力清单；确定目标岗位；列出3个发展方向。", "Complete a capability inventory; define a target role; identify three development directions."],

  ["能力强化", "Capability Building"],
  ["针对目标岗位补足关键技能，强化职场品牌与可见度。", "Build the key skills required for your target role and strengthen your professional brand and visibility."],
  ["完成一项技能提升；发布一篇专业内容或案例；建立行业人脉3-5人。", "Complete one skill-development goal; publish one professional article or case; build three to five relevant industry connections."],

  ["主动出击", "Proactive Advancement"],
  ["主动争取新项目、晋升机会或更优质职位。", "Proactively pursue new projects, advancement opportunities, or stronger career positions."],
  ["提交一份晋升方案或谈判；争取一个核心项目负责权。", "Submit one promotion proposal or conduct one advancement discussion; secure responsibility for one core project."],

  // 财富增长
  ["财务诊断", "Financial Review"],
  ["分析现有收入、支出、储蓄和资产结构，找出可优化点。", "Review current income, expenses, savings, and asset structure to identify areas for improvement."],
  ["完成月度现金流表；识别3个可节省支出点；找到1个增收机会。", "Complete a monthly cash-flow statement; identify three areas to reduce spending; identify one income-growth opportunity."],

  ["增收优化", "Income Growth Optimization"],
  ["开发副业或第二收入来源，优化现有技能的变现方式。", "Develop a side business or second income source and improve how existing skills are monetized."],
  ["启动一个副业测试；完成财务规划表；建立储蓄自动化机制。", "Launch one side-business test; complete a financial planning worksheet; establish an automated savings mechanism."],

  ["资产布局", "Asset Planning"],
  ["学习基础理财知识，建立适合自己的资产配置框架。", "Learn core financial-planning principles and build an asset-allocation framework suited to your circumstances."],
  ["完成一个理财学习计划；投入小额测试资金；建立资产追踪系统。", "Complete a financial-learning plan; use a small amount of test capital; establish an asset-tracking system."],

  // 创业发展
  ["创业调研", "Venture Research"],
  ["研究目标市场、竞争格局、潜在客户和痛点需求。", "Research the target market, competitive landscape, potential customers, and key customer needs."],
  ["完成市场调研报告；访谈5个潜在客户；确定1个核心方向。", "Complete a market research report; interview five potential customers; define one core direction."],

  ["最小可行", "Minimum Viable Test"],
  ["用最低成本制作最小可行产品或服务，快速测试市场。", "Create a minimum viable product or service at low cost and test the market quickly."],
  ["完成MVP方案；获得首批客户反馈；验证核心价值主张。", "Complete an MVP plan; obtain initial customer feedback; validate the core value proposition."],

  ["迭代验证", "Iterate & Validate"],
  ["根据反馈优化产品，小规模获客验证商业模式。", "Improve the product based on feedback and validate the business model through small-scale customer acquisition."],
  ["完成一轮产品迭代；获得5-10个付费用户；确定下阶段扩张策略。", "Complete one product iteration; obtain five to ten paying users; define the next-stage expansion strategy."],

  // 学习提升
  ["学习规划", "Learning Plan"],
  ["制定系统化学习目标，选定核心学习资源和路径。", "Set structured learning goals and select core learning resources and pathways."],
  ["确定一个核心技能目标；制定30-90天学习计划；完成入门模块。", "Define one core skill goal; create a 30-to-90-day learning plan; complete the introductory module."],

  ["深度学习", "Deep Learning"],
  ["进入深度学习阶段，完成核心课程并开始实践应用。", "Move into deeper learning, complete the core course material, and begin practical application."],
  ["完成主干课程70%；完成一个实践项目或案例；整理学习笔记。", "Complete 70% of the core course; finish one practical project or case; organize learning notes."],

  ["输出应用", "Apply & Share"],
  ["将学习成果转化为可分享的内容或实际工作成果。", "Convert learning outcomes into shareable content or practical work results."],
  ["发布一篇学习总结；在实际工作中应用新技能；考虑证书或展示机会。", "Publish one learning summary; apply the new skill in real work; consider certification or a presentation opportunity."],

  // 稳定生活
  ["稳定评估", "Stability Assessment"],
  ["评估当前生活各方面的稳定度，找出核心不稳定因素。", "Assess stability across key areas of life and identify the main sources of instability."],
  ["完成生活满意度评估；识别3个需优化的不稳定点；建立日常节奏。", "Complete a life-satisfaction assessment; identify three instability points to improve; establish a daily routine."],

  ["基础优化", "Foundation Improvement"],
  ["优化工作生活平衡，建立有利于稳定的习惯与系统。", "Improve work-life balance and build habits and systems that support stability."],
  ["建立稳定作息；完成一项生活优化计划；强化健康管理习惯。", "Establish a consistent routine; complete one lifestyle improvement plan; strengthen health-management habits."],

  ["长期规划", "Long-Term Planning"],
  ["制定中期稳定发展计划，确保主要方向有可持续性。", "Create a medium-term stability plan and ensure that your main direction is sustainable."],
  ["完成3-5年稳定规划；建立家庭或个人财务安全网；确立长期方向。", "Complete a three-to-five-year stability plan; establish a family or personal financial safety net; define a long-term direction."],

  // 贵人合作
  ["圈子梳理", "Network Review"],
  ["梳理现有人脉，识别贵人潜力与合作机会。", "Review your current network and identify potential mentors and collaboration opportunities."],
  ["完成人脉价值评估；确定2-3个重点经营关系；联系1位潜在贵人。", "Complete a network-value review; identify two to three priority relationships; contact one potential mentor."],

  ["关系深化", "Relationship Development"],
  ["主动创造价值，深化与核心贵人的互动频率。", "Create value proactively and deepen interaction with key mentors or supporters."],
  ["参加1-2个行业活动；为贵人创造一次实质帮助；建立持续沟通机制。", "Attend one to two industry events; provide one meaningful form of help to a key contact; establish an ongoing communication rhythm."],

  ["合作落地", "Collaboration Execution"],
  ["推进一个实际的合作项目或互利计划。", "Move one practical collaboration or mutually beneficial plan into execution."],
  ["启动一个合作项目或介绍；完成合作框架协议；建立长期互惠机制。", "Start one collaboration or referral; complete a collaboration framework; establish a long-term reciprocal mechanism."],

  // 内容创作
  ["定位规划", "Content Positioning"],
  ["确定内容方向、目标受众和核心价值主张。", "Define the content direction, target audience, and core value proposition."],
  ["完成内容定位文档；发布首批10条内容；确定内容格式和节奏。", "Complete a content-positioning document; publish the first ten pieces of content; define the content format and cadence."],

  ["稳定输出", "Consistent Publishing"],
  ["保持持续内容输出节奏，积累粉丝和初步影响力。", "Maintain a consistent publishing rhythm and build an initial audience and level of influence."],
  ["累计发布30条内容；建立粉丝基础100+；分析数据找出最受欢迎方向。", "Publish 30 pieces of content in total; build an audience base of 100+; analyze data to identify the strongest content direction."],

  ["变现测试", "Monetization Test"],
  ["探索内容变现路径，测试付费咨询、课程或广告合作可行性。", "Explore content-monetization pathways and test the viability of paid consulting, courses, or advertising partnerships."],
  ["完成一次变现测试；获得首批付费用户或合作机会；规划下一阶段增长。", "Complete one monetization test; obtain initial paying users or partnership opportunities; plan the next growth stage."],

  // 管理领导
  ["领导力评估", "Leadership Assessment"],
  ["评估当前领导力优势与短板，建立提升计划。", "Assess current leadership strengths and development gaps and create an improvement plan."],
  ["完成领导力评估；确定1-2个核心提升领域；建立与团队的沟通节奏。", "Complete a leadership assessment; define one to two core development areas; establish a regular communication rhythm with the team."],

  ["团队建设", "Team Development"],
  ["优化团队管理方式，提升团队执行力和凝聚力。", "Improve team-management practices to strengthen execution and cohesion."],
  ["建立明确的目标与反馈机制；完成一次团队建设活动；优化工作分配。", "Establish clear goals and feedback mechanisms; complete one team-building activity; improve work allocation."],

  ["战略落地", "Strategy Execution"],
  ["推动一个核心战略项目落地，展现领导成果。", "Drive one core strategic project into execution and demonstrate leadership results."],
  ["完成一个核心项目或转型计划；建立绩效追踪系统；扩大管理影响力。", "Complete one core project or transformation plan; establish a performance-tracking system; expand management impact."],

  // Day Master risk text used by Sheet14
  ["执行力和承压能力可能不足，重大转变前需先补充资源与支持系统。", "Execution capacity and resilience under pressure may be limited. Strengthen resources and support systems before making major changes."],
  ["精力充沛但可能过于强硬，容易在合作关系中产生摩擦。", "Strong energy and drive may become overly forceful, which can create friction in collaborative relationships."],
  ["当前日主状态相对平衡，整体风险较低，重点在持续执行与避免分散。", "The current Day Master state is relatively balanced, with lower overall risk. The priority is consistent execution and avoiding unnecessary dispersion of focus."],

  // Personal Year risk text actually reachable in Sheet14 validated path
  ["新周期开始，方向感尚未稳固，容易在众多机会中分散精力，初期决策可能缺乏充分论证。", "A new cycle is beginning, and direction may not yet be fully established. Too many opportunities can dilute focus, and early decisions may need stronger validation."],
  ["合作与人际关系是本年关键，单打独斗效率下降，依赖性与合作摩擦风险上升。", "Collaboration and relationships are central this year. Working alone may be less effective, while dependency and partnership friction may increase."],
  ["创意与表达能量旺盛，计划变动频率高，易于分散在众多想法而忽略执行落地。", "Creative and expressive momentum is strong, but frequent changes in plans may spread attention across too many ideas and weaken execution."],
  ["本年是建立系统与基础的关键期，进展可能看似缓慢，容易产生急于看到成果的焦虑。", "This is an important year for building systems and foundations. Progress may appear slower, which can create pressure for faster visible results."],
  ["变化机会多但也容易冲动决策，风险敞口较大。", "Opportunities for change are more frequent, but impulsive decisions can increase risk exposure."],
  ["责任与义务感强烈，家庭或团队需求可能增加，个人边界模糊导致精力分散。", "Responsibility and obligation may increase, while greater family or team demands can blur personal boundaries and disperse energy."],
  ["内省与学习能量强，外部市场反应可能偏慢，容易因成果不明显而产生怀疑与动摇。", "Reflection and learning are strong themes, while external results may develop more slowly, which can create doubt when progress is not immediately visible."],
  ["财务与权力机会集中出现，决策影响力放大；把握不当可能带来高杠杆风险，过于保守则错失窗口。", "Financial and influence-related opportunities may become more concentrated, increasing the impact of decisions. Poor judgment can magnify leverage risk, while excessive caution may miss worthwhile windows."],
  ["本年是周期收尾期，适合整理而非新开始，强行冲刺效果有限。", "This year is a cycle-closing period and is better suited to consolidation than major new starts. Forcing rapid expansion may have limited benefit."],
]);


const SHEET15 = pairs([
  // ── B15 / B16 / B17 — conceptual direction outputs ────────────────
  ["北方", "North"],
  ["东北方/西南方/中部", "Northeast / Southwest / Central"],
  ["东方/东南方", "East / Southeast"],
  ["南方", "South"],
  ["西方/西北方", "West / Northwest"],

  ["北方/东方/东南方", "North / East / Southeast"],
  ["北方/南方", "North / South"],
  ["参考主要方位", "Refer to the primary direction"],
  ["东北方/西南方", "Northeast / Southwest"],
  ["东方/东南方/南方", "East / Southeast / South"],
  ["南方/东北方/西南方", "South / Northeast / Southwest"],
  ["南方/西方", "South / West"],
  ["西方/西北方/北方", "West / Northwest / North"],

  ["按调和方向减少", "Reduce emphasis according to the balancing direction"],
  ["南方/东方/东南方", "South / East / Southeast"],

  // ── B18 — recommended industries, exact whole-string DB/fallback ──
  [
    "参考11_事业财富引擎Top 5行业",
    "Refer to the Top 5 industries from the Career & Wealth Engine",
  ],
  [
    "房地产、仓储运营、行政管理、制造生产、餐饮服务、农业、稳定型服务业、资产管理",
    "Real estate, warehousing operations, administration, manufacturing, food and beverage services, agriculture, stable service industries, and asset management",
  ],
  [
    "教育培训、咨询顾问、内容创作、文化传播、自媒体、人力资源",
    "Education and training, consulting and advisory, content creation, cultural communication, creator media, and human resources",
  ],
  [
    "金融会计、法律合规、科技系统、企业管理、制度型行业",
    "Finance and accounting, legal and compliance, technology systems, business management, and process-driven industries",
  ],
  [
    "贸易销售、物流供应链、旅游流通、电商平台、跨境业务",
    "Trade and sales, logistics and supply chain, travel and distribution, e-commerce platforms, and cross-border business",
  ],
  [
    "品牌营销、科技AI、媒体传播、销售业务、直播、自媒体",
    "Brand and marketing, technology and AI, media communication, sales, live streaming, and creator media",
  ],

  // ── B19 — recommended roles, exact whole-string DB/fallback ───────
  [
    "财务会计、合规管理、系统管理、管理领导、数据分析",
    "Finance and accounting, compliance management, systems management, management and leadership, and data analysis",
  ],
  [
    "参考11_事业财富引擎Top 5岗位",
    "Refer to the Top 5 roles from the Career & Wealth Engine",
  ],
  [
    "培训师、顾问、内容策划、品牌规划、客户关系、HR发展",
    "Trainer, consultant, content strategist, brand planner, client relations, and HR development",
  ],
  [
    "品牌营销、销售顾问、内容创作者、讲师、市场推广",
    "Brand and marketing, sales consultant, content creator, lecturer or trainer, and market promotion",
  ],
  [
    "销售业务、物流协调、市场开发、客户关系、电商运营",
    "Sales and business development, logistics coordination, market development, client relations, and e-commerce operations",
  ],
  [
    "运营管理、行政主管、仓储管理、资产管理、项目协调、后勤规划",
    "Operations management, administration supervision, warehouse management, asset management, project coordination, and support planning",
  ],

  // ── B20 — suitable environment ─────────────────────────────────────
  [
    "保持均衡，随目标调整环境",
    "Maintain balance and adjust the environment according to the current goal",
  ],
  [
    "高曝光、表达机会多、市场活跃、创新传播型环境",
    "An environment with high visibility, frequent opportunities for expression, active markets, and innovative communication",
  ],
  [
    "规则清晰、专业要求高、制度完整、执行纪律强的环境",
    "An environment with clear rules, high professional standards, complete systems, and strong execution discipline",
  ],
  [
    "流动性强、人际互动多、市场变化快、资源连接型环境",
    "A dynamic environment with frequent interpersonal interaction, fast-changing markets, and strong resource connectivity",
  ],
  [
    "稳定、长期、资源整合、基础建设、制度清楚的环境",
    "A stable, long-term environment focused on resource integration, foundation building, and clear systems",
  ],
  [
    "学习型、成长型、文化型、规划型环境",
    "A learning-oriented, growth-oriented, cultural, and planning-focused environment",
  ],

  // ── B21 — unsuitable environment / reduce items ───────────────────
  [
    "避免过度分散资源",
    "Avoid spreading resources too thinly",
  ],
  [
    "减少短期投机；不宜频繁更换发展方向",
    "Reduce short-term speculation; avoid changing development direction too frequently",
  ],
  [
    "减少封闭保守；不宜拒绝新合作机会",
    "Reduce excessive conservatism and avoid rejecting new collaboration opportunities without consideration",
  ],
  [
    "减少规则混乱环境；不宜在专业未成熟时过早扩张",
    "Reduce exposure to poorly structured environments; avoid expanding too early before professional capability is mature",
  ],
  [
    "减少过度低调；不宜在高曝光机会出现时退缩",
    "Avoid being excessively low-profile; do not withdraw automatically when appropriate high-visibility opportunities arise",
  ],
  [
    "减少频繁转换方向；不宜轻易放弃已建立的基础",
    "Reduce frequent changes in direction; avoid abandoning foundations that have already been established",
  ],

  // ── B22 — career advice ────────────────────────────────────────────
  [
    "按当前目标选择最高指数方向重点突破。",
    "Use the current goal to identify the highest-index direction and focus development there.",
  ],
  [
    "火元素代表传播力，适合主动建立品牌曝光，通过营销表达创造市场价值。",
    "The Fire element represents communication and visibility. It supports proactive brand exposure and creating market value through marketing and expression.",
  ],
  [
    "金元素代表精准，适合深耕专业领域，通过制度执行创造高价值。",
    "The Metal element represents precision. It supports deeper professional specialization and creating value through disciplined execution and systems.",
  ],
  [
    "木元素代表成长，适合通过学习积累建立专业影响力，发展教育或内容型事业。",
    "The Wood element represents growth. It supports building professional influence through learning and developing education- or content-oriented work.",
  ],
  [
    "水元素代表流通，适合以人脉和渠道为核心，通过多元流通创造变现机会。",
    "The Water element represents flow and connectivity. It supports using networks and channels to create diversified monetization opportunities.",
  ],
  [
    "土元素代表稳定，适合系统化管理和长期积累，建立资产基础，稳步发展资源整合型事业。",
    "The Earth element represents stability. It supports systematic management, long-term accumulation, building an asset foundation, and steady resource-integration development.",
  ],

  // ── B24 — venture advice ───────────────────────────────────────────
  [
    "电商、贸易、销售类创业适合度高，快速起步、快速迭代。",
    "E-commerce, trading, and sales-oriented ventures are relatively suitable; start efficiently and iterate quickly.",
  ],
  [
    "品牌营销、自媒体或销售类创业适合度高，把握高能见度窗口。",
    "Brand marketing, creator-media, and sales-oriented ventures are relatively suitable; make use of periods of higher visibility.",
  ],
  [
    "实体型、服务型创业适合度高，需要充分的资金和制度准备。",
    "Physical or service-oriented ventures are relatively suitable, with sufficient financial and operational preparation.",
  ],
  [
    "适合教育、内容或咨询类创业，先建立专业声誉再扩大变现。",
    "Education-, content-, or consulting-oriented ventures are suitable; establish professional credibility before expanding monetization.",
  ],
  [
    "专业型创业适合度高，需要充分的专业资质和精准市场定位。",
    "Professionally specialized ventures are relatively suitable and require strong qualifications and precise market positioning.",
  ],

  // ── B26 — fiveElementParallel finite passthrough ───────────────────
  ["无并列", "No tied minimum elements"],
  [
    "⚠️ 并列提示：2个元素同为最低，建议在主要补益方向基础上兼顾其他偏弱元素。",
    "⚠️ Tie notice: 2 elements share the lowest level. Keep the primary support direction while also considering the other weaker element.",
  ],
  [
    "⚠️ 并列提示：3个元素同为最低，建议在主要补益方向基础上兼顾其他偏弱元素。",
    "⚠️ Tie notice: 3 elements share the lowest level. Keep the primary support direction while also considering the other weaker elements.",
  ],
  [
    "⚠️ 并列提示：4个元素同为最低，建议在主要补益方向基础上兼顾其他偏弱元素。",
    "⚠️ Tie notice: 4 elements share the lowest level. Keep the primary support direction while also considering the other weaker elements.",
  ],
  [
    "⚠️ 并列提示：5个元素同为最低，建议在主要补益方向基础上兼顾其他偏弱元素。",
    "⚠️ Tie notice: 5 elements share the lowest level. Keep the primary support direction while also considering the other weaker elements.",
  ],

  // ── B28 / B29 / B30 — independent direction-with-element ladders ──
  ["北方（水）", "North (Water)"],
  ["东北方 / 西南方 / 中部（土）", "Northeast / Southwest / Central (Earth)"],
  ["东方 / 东南方（木）", "East / Southeast (Wood)"],
  ["南方（火）", "South (Fire)"],
  ["西方 / 西北方（金）", "West / Northwest (Metal)"],

  [
    "北方（水） / 东方 / 东南方（木）",
    "North (Water) / East / Southeast (Wood)",
  ],
  [
    "北方（水） / 南方（火）",
    "North (Water) / South (Fire)",
  ],
  [
    "东方 / 东南方（木） / 南方（火）",
    "East / Southeast (Wood) / South (Fire)",
  ],
  [
    "南方（火） / 东北方 / 西南方 / 中部（土）",
    "South (Fire) / Northeast / Southwest / Central (Earth)",
  ],
  [
    "南方（火） / 西方 / 西北方（金）",
    "South (Fire) / West / Northwest (Metal)",
  ],
  [
    "西方 / 西北方（金） / 北方（水）",
    "West / Northwest (Metal) / North (Water)",
  ],
  [
    "南方（火） / 东方 / 东南方（木）",
    "South (Fire) / East / Southeast (Wood)",
  ],
]);

const CATALOGS = Object.freeze({
  sheet12: SHEET12,
  sheet13: SHEET13,
  sheet14: SHEET14,
  sheet15: SHEET15,
});

export function localizeReportNarrative(locale, domain, value) {
  if (typeof value !== "string") return value;
  if (locale !== "en") return value;

  const catalog = CATALOGS[domain];
  if (!catalog) return value;

  return Object.prototype.hasOwnProperty.call(catalog, value)
    ? catalog[value]
    : value;
}

export const __REPORT_NARRATIVE_CATALOG_TEST_ONLY__ = Object.freeze({
  sheet12: SHEET12,
  sheet13: SHEET13,
  sheet14: SHEET14,
  sheet15: SHEET15,
});
